import * as React from "react";
import { useIsMutating } from "@tanstack/react-query";
import {
    useLocation,
    useNavigate,
    useParams,
    useSearchParams,
} from "react-router-dom";
import {
    ActionButton,
    Button,
    Card,
    Dialog,
    DialogContent,
    Tabs,
} from "@jhool-io/fe-components";
import { useFetchSessionNote } from "../../../../hooks/queries/note";
import {
    checkIfRichTextHasTextContent,
    cn,
    convertUrlSearchParamsToObject,
    removeEnumUnderscore,
} from "../../../../utils/helpers";
import styles from "./SingleBillViewWrapper.module.scss";
import { ViewEditorText } from "../../../../components/TextEditor/ViewEditorText/ViewEditorText";
import InvoiceNotes from "../InvoiceNotes/InvoiceNotes";
import HarmonizedView from "../../../../shared-ui/HarmonizedView/HarmonizedView";
import PendingResolutionForm from "../PendingResolutionForm/PendingResolutionForm";
import { useFetchInvoices } from "../../hooks/queries/billing.queries";
import { useFetchRemitClaims } from "../../../remits/hooks/remits.queries";
import TabItemLoader from "../../../../shared-ui/TabItemLoader/TabItemLoader";
import ClaimsWrapper from "../../../claims/components/ClaimsWrapper/ClaimsWrapper";
import Loader from "../../../../shared-ui/Loader/Loader";
import ChevronRightIcon from "../../../../icons/ChevronRight";
import ChevronLeftIcon from "../../../../icons/ChevronLeft";
import EditIcon from "../../../../icons/Edit";
import { usePendingResolutionDialogText } from "../../hooks/helpers.hooks";
import { NoteTypes } from "../../../notes/types/notes.types";
import {
    BillStatusesWithIssueDetails,
    IBillingFilterTypes,
} from "../../types/billing.types";
import NoteNavigation from "../../../notes/components/NoteNavigation/NoteNavigation";

type Modals = "change-cpt" | "pending-resolution";

const NoteItemsWrapper = React.lazy(
    () => import("../../../notes/components/NoteItemsWrapper/NoteItemsWrapper")
);

const PaymentTabWrapper = React.lazy(
    () => import("../PaymentTabWrapper/PaymentTabWrapper")
);

const RemitsWrapper = React.lazy(
    () => import("../../../remits/components/RemitsWrapper/RemitsWrapper")
);

export default function SingleBillViewWrapper() {
    const [modalInView, setModalInView] = React.useState<Modals | null>(null);
    // Get id from url params
    const params = useParams();
    const location = useLocation();
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();

    const clientId = params.clientId as string;

    const noteId = params.noteId as string;

    const { data, isLoading, isSuccess } = useFetchSessionNote(
        clientId,
        noteId,
        Boolean(clientId) && Boolean(noteId)
    );

    const remitsData = useFetchRemitClaims({
        note_id: noteId,
    });

    const pendingResolutionDialogText = usePendingResolutionDialogText();

    const archivedDefaultTab = data?.data.is_archived ? "note" : "payments";
    const requestedTab = searchParams.get("tab");
    const canViewClaimsTab =
        data?.data.is_eligible_for_claim_creation ||
        Boolean(data?.data.claim_id);
    const isRemitsTabDisabled =
        (remitsData.isSuccess && remitsData.data.data.length === 0) ||
        (!remitsData.isInitialLoading &&
            !remitsData.isError &&
            !remitsData.isSuccess);
    const isRequestedTabUnavailable =
        !["note", "claims", "payments", "remit"].includes(requestedTab ?? "") ||
        (requestedTab === "claims" && !canViewClaimsTab) ||
        (requestedTab === "payments" &&
            data?.data.is_archived &&
            !data?.data.invoice_id) ||
        (requestedTab === "remit" && isRemitsTabDisabled);
    const tabInUrl =
        !requestedTab || isRequestedTabUnavailable
            ? archivedDefaultTab
            : requestedTab;

    const viewFrom = searchParams.get("viewFrom");

    /* Get an object of the searchparams from the invoices list
     we get this from location.state which was set on the handleRowClick function
     of each row on the invoices list */
    const billsPageParamsObj = React.useMemo(
        () =>
            location.state
                ? convertUrlSearchParamsToObject<IBillingFilterTypes>(
                      location.state
                  )
                : null,
        [location.state]
    );

    const invoiceStatusQueryString = React.useMemo(() => {
        const status = billsPageParamsObj?.invoice_status;

        if (!status || status.length === 0) {
            return ["open"];
        }

        if (status.length === 1 && status[0] === "all") {
            return [];
        }

        return status;
    }, [billsPageParamsObj?.invoice_status]);

    const billsPageNumber = Number(billsPageParamsObj?.page ?? 1);
    const currentBillsPage =
        Number.isNaN(billsPageNumber) || billsPageNumber < 1
            ? 1
            : billsPageNumber;

    // Hook for fetching all invoices
    const invoicesList = useFetchInvoices({
        search_string:
            billsPageParamsObj?.search_string ??
            billsPageParamsObj?.client_name ??
            "",
        client_id: billsPageParamsObj?.client_id ?? undefined,
        author_id: billsPageParamsObj?.author_id ?? undefined,
        provider_name: billsPageParamsObj?.provider_name ?? "",
        insurance_provider_ids:
            billsPageParamsObj?.insurance_provider_ids ?? [],
        invoice_status: invoiceStatusQueryString,
        cpt_code: billsPageParamsObj?.cpt_code ?? "",
        from_date: billsPageParamsObj?.from_date,
        to_date: billsPageParamsObj?.to_date,
        page: currentBillsPage,
        limit: 300,
        sort_attribute: billsPageParamsObj?.sort_attribute ?? undefined,
        sort_order: billsPageParamsObj?.sort_order ?? undefined,
    });

    const arrayOfInvoicesToNavigateWith = React.useMemo(
        () =>
            invoicesList?.data?.data.map((invoice) => ({
                client_id: invoice.client.client_id ?? "",
                note_id: invoice.note_id,
                invoice_id: invoice.invoice_id,
            })) ?? [],
        [invoicesList?.data?.data]
    );

    const getCurrentInvoiceIndex = React.useCallback(() => {
        const currentIndex = arrayOfInvoicesToNavigateWith.findIndex(
            (invoice) => invoice.note_id === noteId
        );

        return currentIndex;
    }, [arrayOfInvoicesToNavigateWith, noteId]);

    const currentInvoiceIndex = getCurrentInvoiceIndex();

    const handlePrevButtonClick = React.useCallback(() => {
        const currentIndex = getCurrentInvoiceIndex();
        if (currentIndex === -1 || currentIndex === 0) return;
        navigate(
            `/billing-and-claims/${
                arrayOfInvoicesToNavigateWith[currentIndex - 1]?.client_id ?? ""
            }/${
                arrayOfInvoicesToNavigateWith[currentIndex - 1]?.note_id
            }?tab=${encodeURIComponent(tabInUrl)}`,
            {
                state: location.state ?? null,
            }
        );
    }, [
        arrayOfInvoicesToNavigateWith,
        getCurrentInvoiceIndex,
        location.state,
        navigate,
        tabInUrl,
    ]);

    const handleNextButtonClick = React.useCallback(() => {
        const currentIndex = getCurrentInvoiceIndex();
        if (
            currentIndex === -1 ||
            currentIndex === arrayOfInvoicesToNavigateWith.length - 1
        )
            return;
        navigate(
            `/billing-and-claims/${
                arrayOfInvoicesToNavigateWith[currentIndex + 1].client_id ?? ""
            }/${
                arrayOfInvoicesToNavigateWith[currentIndex + 1].note_id
            }?tab=${encodeURIComponent(tabInUrl)}`,
            {
                state: location.state ?? null,
            }
        );
    }, [
        arrayOfInvoicesToNavigateWith,
        getCurrentInvoiceIndex,
        location.state,
        navigate,
        tabInUrl,
    ]);

    const handleSetTabInUrl = React.useCallback(
        (tab: string) => {
            const nextSearchParams = new URLSearchParams(searchParams);
            nextSearchParams.set("tab", tab);
            setSearchParams(nextSearchParams, {
                state: location.state ?? null,
            });
        },
        [location.state, searchParams, setSearchParams]
    );

    const tabItems = React.useMemo(
        () => [
            {
                label: "Session Note",
                key: "note",
                children: (
                    <div>
                        <React.Suspense fallback={<TabItemLoader />}>
                            <NoteItemsWrapper />
                        </React.Suspense>
                    </div>
                ),
                onClick: () => handleSetTabInUrl("note"),
            },
            {
                label: "Claims",
                key: "claims",
                children: (
                    <div>
                        <React.Suspense fallback={<TabItemLoader />}>
                            <ClaimsWrapper
                                allowUserGenerate={canViewClaimsTab}
                            />
                        </React.Suspense>
                    </div>
                ),
                onClick: () => handleSetTabInUrl("claims"),
                disabled: !canViewClaimsTab,
            },
            {
                label: "Payments History",
                key: "payments",
                children: (
                    <React.Suspense fallback={<TabItemLoader />}>
                        <PaymentTabWrapper />
                    </React.Suspense>
                ),
                onClick: () => handleSetTabInUrl("payments"),
                disabled: data?.data.is_archived && !data?.data.invoice_id,
            },
            {
                key: "remit",
                label: "Remits",
                children: (
                    <div>
                        <React.Suspense fallback={<TabItemLoader />}>
                            <RemitsWrapper />
                        </React.Suspense>
                    </div>
                ),
                onClick: () => handleSetTabInUrl("remit"),
                disabled: isRemitsTabDisabled,
            },
        ],
        [
            canViewClaimsTab,
            data?.data.invoice_id,
            data?.data.is_archived,
            handleSetTabInUrl,
            isRemitsTabDisabled,
        ]
    );

    const updateInVoiceIsMutating = useIsMutating(["update-invoice"]);

    return (
        <div>
            <Dialog open={modalInView === "pending-resolution"}>
                <DialogContent
                    title={pendingResolutionDialogText}
                    handleCloseDialog={() => {
                        setModalInView(null);
                        searchParams.delete("payment_failed");
                        setSearchParams(searchParams);
                    }}
                    variant="center"
                    showFooter
                    cancelText="Cancel"
                    saveText={pendingResolutionDialogText}
                    submitBtnFormValue="pending-resolution"
                    isSubmitBtnDisabled={updateInVoiceIsMutating > 0}
                    isCancelBtnDisabled={updateInVoiceIsMutating > 0}
                >
                    <PendingResolutionForm
                        onFormSubmit={() => setModalInView(null)}
                    />
                </DialogContent>
            </Dialog>
            {isLoading ? <Loader /> : null}

            {isSuccess && data && (
                <div className={styles.header}>
                    <div className={styles.navigation}>
                        {viewFrom === "session_note" ? (
                            <NoteNavigation
                                currentNoteId={noteId}
                                basePath="billing-and-claims"
                                viewFrom={viewFrom}
                            />
                        ) : (
                            <>
                                {invoicesList.isLoading && (
                                    <div className={styles.navigation_loading}>
                                        <Loader loaderType="inline" />
                                        <Loader loaderType="inline" />
                                    </div>
                                )}
                                {invoicesList.error && (
                                    <div className={styles.navigation_error}>
                                        Cannot display navigation buttons at
                                        this time, try reloading the page
                                    </div>
                                )}
                                {invoicesList.data && (
                                    <div className={styles.navigation_btns}>
                                        <ActionButton
                                            size="auto"
                                            variant="normal"
                                            disabled={
                                                currentInvoiceIndex === -1 ||
                                                currentInvoiceIndex === 0
                                            }
                                            onClick={handlePrevButtonClick}
                                            actionTitle="Previous bill"
                                            className={
                                                "hover:opacity-70 " +
                                                "[transition:opacity_150ms_ease]"
                                            }
                                        >
                                            <ChevronLeftIcon /> Prev
                                        </ActionButton>
                                        <ActionButton
                                            size="auto"
                                            variant="normal"
                                            disabled={
                                                currentInvoiceIndex === -1 ||
                                                currentInvoiceIndex ===
                                                    arrayOfInvoicesToNavigateWith.length -
                                                        1
                                            }
                                            onClick={handleNextButtonClick}
                                            actionTitle="Next bill"
                                            className={
                                                "hover:opacity-70 " +
                                                "[transition:opacity_150ms_ease]"
                                            }
                                        >
                                            Next
                                            <ChevronRightIcon />
                                        </ActionButton>
                                    </div>
                                )}
                            </>
                        )}
                    </div>

                    <div className={styles.infonoteswrapper}>
                        <div className={styles.infowrapper}>
                            <div>
                                <HarmonizedView />
                            </div>
                            {BillStatusesWithIssueDetails.includes(
                                data.data.invoice_status || ""
                            ) ? (
                                <Card classNames="mt-[--spacing-items] gap-[--spacing-items]">
                                    <div
                                        className={styles.prcard_contentwrapper}
                                    >
                                        <span className={styles.prcard_status}>
                                            {removeEnumUnderscore(
                                                data.data.issue_category || ""
                                            )}
                                            <Button
                                                size="auto"
                                                variant="can-hover"
                                                onClick={() =>
                                                    setModalInView(
                                                        "pending-resolution"
                                                    )
                                                }
                                            >
                                                <EditIcon />
                                            </Button>
                                        </span>

                                        <div className={styles.prcard_details}>
                                            {data.data.issue_notes &&
                                            checkIfRichTextHasTextContent(
                                                data.data.issue_notes
                                            ) ? (
                                                <ViewEditorText
                                                    text={data.data.issue_notes}
                                                />
                                            ) : (
                                                <span className={styles.empty}>
                                                    No details provided
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                </Card>
                            ) : null}
                        </div>

                        {data.data.invoice_id ? (
                            <div className={styles.noteswrapper}>
                                <InvoiceNotes
                                    invoiceId={data.data.invoice_id}
                                    isNoShowNote={
                                        data.data.type ===
                                        NoteTypes.CANCELLATION_NOTE
                                    }
                                />
                            </div>
                        ) : null}
                    </div>

                    <Tabs
                        key={tabInUrl}
                        items={tabItems}
                        className={cn("!w-[100%] !items-end")}
                        defaultActiveKey={tabInUrl}
                        size="small"
                    />
                </div>
            )}
        </div>
    );
}
